/** @jsxImportSource @emotion/react */

// libraries
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { css } from "@emotion/react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";

// model, data
import { Column } from "data/table.data";
import { DASHBOARD_COLS, JOURNAL_DASHBOARD_COLS } from "./column.data";
import { SAMPLE_BUSINESS_DATA } from "data/sample.data";
import {
  HALF_AND_QUARTERS,
  HalfYear,
  MONTHS,
  Quarter,
  YearMonth,
} from "data/period.data";
import {
  Business,
  MenuType,
  StoreType,
  SUB_TYPE_PARAM_KEYS,
  SubType,
} from "models/business.model";

// components
import Table from "components/common/Table";
import BaseSelect from "components/common/Select/Base";
import BaseInput from "components/common/Input/Base";
import BaseButton from "components/common/Button/Base";
import BaseCheck from "components/common/Check/Base";
import AlertDialog, { AlertData } from "components/common/Dialog/Alert";
import SubtitleCard from "components/common/Card/Subtitle/SubtitleCard";

// utils, api
import {
  BaseParamData,
  getExtraInvoiceStores,
  getInchargeCandidates,
  getStores,
  getUserInfo,
  updateIncharge,
} from "pages/remotes";
import {
  convertDateToPeriod,
  convertQuarterAndHalfToDate,
  convertYearMonthToDate,
  reverseArray,
  sortList,
} from "utils/converter";
import { useMyChargeOnlyFilter } from "utils/hook";
import { isPageNavigatingAway } from "utils/navigation";
import { PALETTE } from "styles/base/variables";

// icons
import { ReactComponent as ChartIcon } from "assets/img/icon/chart.svg";
import { ReactComponent as CompletedIcon } from "assets/img/icon/completed.svg";
import { ReactComponent as UncompletedIcon } from "assets/img/icon/uncompleted.svg";
import { ReactComponent as TotalIcon } from "assets/img/icon/etc.svg";

// 담당자 선택 드롭다운에서 "담당자 없음"을 고르면 이 값을 pendingIncharge로
// 담아뒀다가, 실제 API 호출 시점에는 incharge를 null로 바꿔서 보낸다.
const NO_INCHARGE_OPTION_VALUE = "__NO_INCHARGE__";

function Store(data: { storeType: StoreType; subType?: SubType }) {
  const storeType: StoreType = data.storeType;
  const subType: SubType | undefined = data.subType;
  const menuType = (
    subType ? `${storeType}-${subType.toLowerCase()}` : storeType
  ) as MenuType;

  const [searchParams, setSearchParams] = useSearchParams();
  const [spStartDate, spEndDate, spSort, spSample] = [
    searchParams.get("startDate"),
    searchParams.get("endDate"),
    searchParams.get("sort"),
    searchParams.get("sample"),
  ];
  const inputKeyword = useRef<HTMLInputElement>(null);

  // 탭(통장/카드/매입세금/매출세금 등) 전환은 window.location.href로 전체
  // 새로고침을 일으키는데, 이건 즉시 일어나지 않는다 — 현재 탭이 API 응답을
  // 기다리는 동안 사용자가 다른 탭을 클릭하면, 실제 페이지 이동이 일어나기
  // 전 짧은 틈에 이 응답이 도착해 setSearchParams를 호출할 수 있다. 그러면
  // 주소창(및 화면)이 방금 떠난 탭으로 되돌아가 버린다. pagehide 시점에
  // 이 플래그를 세워서, 이미 떠나는 중이면 응답이 늦게 와도 URL을 건드리지
  // 않도록 막는다.
  const isLeavingPageRef = useRef(false);
  useEffect(() => {
    // effect가 (다시) 실행됐다는 건 지금 화면에 마운트되어 있다는 뜻이므로
    // 매번 false로 되돌린다. 이걸 안 하면 React.StrictMode의 개발 모드
    // mount→cleanup→mount 시뮬레이션에서 cleanup이 한 번 돌면서 이 값이
    // true로 굳어버려, 이후 모든 로딩이 이 가드에 걸려 응답을 영영
    // 무시하게 된다(로딩이 끝나지 않는 것처럼 보임).
    isLeavingPageRef.current = false;

    const markLeaving = () => {
      isLeavingPageRef.current = true;
    };
    window.addEventListener("pagehide", markLeaving);
    return () => {
      window.removeEventListener("pagehide", markLeaving);
      isLeavingPageRef.current = true;
    };
  }, []);

  const [isInitial, setIsInitial] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [alertDialogData, setAlertDialogData] = useState<AlertData>({
    message: "",
  });
  const [columnList, setColumnList] = useState<Column[]>([]);
  const [businessList, setBusinessList] = useState<Business[]>([]);

  const [checkedBusiness, setCheckedBusiness] = useState<Set<string>>(
    new Set()
  );

  // 담당자 설정 모드: 켜지면 행 체크박스 선택이 가능해지고, 선택한 업체들에
  // 담당자를 일괄로 배정할 수 있다.
  const [isAssignMode, setIsAssignMode] = useState(false);
  const [inchargeOptions, setInchargeOptions] = useState<string[]>([]);
  const [isInchargeOptionsLoading, setIsInchargeOptionsLoading] =
    useState(false);
  const [pendingIncharge, setPendingIncharge] = useState("");
  const [isInchargeConfirmOpen, setIsInchargeConfirmOpen] = useState(false);
  const [isInchargeSubmitting, setIsInchargeSubmitting] = useState(false);

  const [sortedBusinessList, setSortedBusinessList] = useState<Business[]>([]);
  const [isFiltered, setIsFiltered] = useState(false);
  const [filteredBusinessList, setFilteredBusinessList] = useState<Business[]>(
    []
  );

  // 로그인 유저 이름 기준으로 "내 담당만 보기" 필터링을 하기 위한 값
  const [myName, setMyName] = useState("");
  const [isMyChargeOnly, setIsMyChargeOnly] = useMyChargeOnlyFilter();
  const [summaryData, setSummaryData] = useState<{
    total: number;
    completed: number;
    uncompleted: number;
    dataTotal: number;
  }>({
    total: 0,
    completed: 0,
    uncompleted: 0,
    dataTotal: 0,
  });

  useEffect(() => {
    setColumnList(
      storeType === "journal" ? JOURNAL_DASHBOARD_COLS : DASHBOARD_COLS
    );
  }, []);

  useEffect(() => {
    getUserInfo()
      .then((res) => setMyName(res.data?.name || ""))
      .catch((err) => console.error(err));
  }, []);

  useEffect(() => {
    if (!isAssignMode || inchargeOptions.length > 0) return;

    setIsInchargeOptionsLoading(true);
    getInchargeCandidates()
      .then((res) => {
        setInchargeOptions(res.data || []);
        setIsInchargeOptionsLoading(false);
      })
      .catch((err) => {
        setIsInchargeOptionsLoading(false);
        const tmpAlertData = { ...alertDialogData };
        tmpAlertData.type = "error";
        tmpAlertData.message =
          err.response?.data.message ||
          "담당자 목록을 불러오던 중 오류가 발생하였습니다.";
        setAlertDialogData(tmpAlertData);
        setIsAlertOpen(true);
      });
  }, [isAssignMode]);

  useEffect(() => {
    setSortedBusinessList(sortList([...businessList], spSort));
  }, [spSort]);

  useEffect(() => {
    const bizListLen = businessList.length;

    // 기간 없을 경우 호출 후 응답으로 기간 설정, 응답으로 기간 설정할 경우 재호출 방지
    if (spStartDate && spEndDate) {
      if (isInitial) {
        setIsInitial(false);
        if (!bizListLen) loadStoreList(storeType);
      } else {
        loadStoreList(storeType);
      }
    } else if (isInitial) {
      loadStoreList(storeType);
    }
  }, [spStartDate, spEndDate]);

  useEffect(() => {
    const keyword = inputKeyword.current?.value || "";
    if (keyword)
      inputKeywordHandler({
        currentTarget: { value: keyword },
      } as React.FormEvent<HTMLInputElement>); // 검색어가 있는 경우 검색어 필터링
  }, [sortedBusinessList]);

  const onSetSummaryData = (dataList: Business[]) => {
    const total = dataList.length;
    let completed = 0;
    let uncompleted = 0;
    let dataTotal = 0;
    if (total) {
      dataList.forEach((data) => {
        dataTotal += data.totalDataNumber;
        if (data.completedDataNumber === data.totalDataNumber) completed++;
        else uncompleted++;
      });
    }

    setSummaryData({
      total,
      completed,
      uncompleted,
      dataTotal,
    });
  };

  const loadStoreList = (type: StoreType) => {
    if (!type) {
      const tmpAlertData = { ...alertDialogData };
      tmpAlertData.type = "error";
      tmpAlertData.message = "업체 형식 오류로 현황을 불러올 수 없습니다.";
      setAlertDialogData(tmpAlertData);
      setIsAlertOpen(true);
      return;
    }

    const startDate = spStartDate;
    const endDate = spEndDate;
    const subTypeKey = SUB_TYPE_PARAM_KEYS[type] as SubType;

    let paramData: BaseParamData = {};
    if (startDate && endDate) paramData = { startDate, endDate };
    if (subType && subTypeKey)
      paramData = {
        ...paramData,
        [subTypeKey]: subType,
      };

    setIsLoading(true);
    const request =
      type === "extra" && subType
        ? getExtraInvoiceStores(subType, paramData)
        : getStores(type, paramData);
    request
      .then((res) => {
        if (isLeavingPageRef.current || isPageNavigatingAway()) return;

        const resData = res.data;
        const startDate = resData.startDate;
        const endDate = resData.endDate;
        const businessInfos = spSample
          ? SAMPLE_BUSINESS_DATA
          : resData.businessAccountingInfos;

        if (startDate && endDate) setSearchParams({ startDate, endDate });
        if (startDate && !endDate) {
          // 시작일만 있는 경우: ONLY 은행 입출금 기간 없이 호출할 경우 가장 최신 월 시작일만 반환, 종료일 반환 없음 (0623 의님 확인)
          const endOfMonth = new Date(startDate);
          endOfMonth.setMonth(endOfMonth.getMonth() + 1);
          endOfMonth.setDate(0); // 마지막 날로 설정
          setSearchParams({
            startDate,
            endDate: endOfMonth.toISOString().split("T")[0],
          });
        }

        if (businessInfos) {
          setBusinessList(businessInfos);
          onSetSummaryData(businessInfos);
          setSortedBusinessList(sortList(businessInfos, spSort));
        } else {
          setBusinessList([]);
          onSetSummaryData([]);
          setSortedBusinessList([]);
        }

        setIsLoading(false);
      })
      .catch((err) => {
        setIsLoading(false);
        const tmpAlertData = { ...alertDialogData };
        tmpAlertData.type = "error";
        tmpAlertData.message =
          err.response?.data.message ||
          "업체 현황을 불러오던 중 오류가 발생하였습니다.";
        setAlertDialogData(tmpAlertData);
        setIsAlertOpen(true);
      });
  };

  const onSelectQuarter = (e: React.MouseEvent<HTMLOptionElement>) => {
    const value = e.currentTarget.value || "";
    const { startDate, endDate } = convertQuarterAndHalfToDate(
      value as Quarter | HalfYear
    );
    setSearchParams({ startDate, endDate });
  };
  const onSelectMonth = (e: React.MouseEvent<HTMLOptionElement>) => {
    const value = e.currentTarget.value || "";
    const { startDate, endDate } = convertYearMonthToDate(value as YearMonth);
    setSearchParams({ startDate, endDate });
  };

  const inputKeywordHandler = (e: React.FormEvent<HTMLInputElement>) => {
    const value = e.currentTarget.value || "";
    const loweredVal = value.toLowerCase();
    if (value) {
      setIsFiltered(true);
      let tmpSortedBusinessList = sortedBusinessList.filter((business) => {
        const loweredName = business.name?.toLowerCase() || "";
        return loweredName.includes(loweredVal);
      });
      setFilteredBusinessList(tmpSortedBusinessList);
    } else {
      setIsFiltered(false);
    }
  };

  const toggleAssignMode = () => {
    setCheckedBusiness(new Set());
    setIsAssignMode((prev) => !prev);
  };

  const selectIncharge = (e: React.MouseEvent<HTMLOptionElement>) => {
    const value = e.currentTarget.value || "";
    if (!value || checkedBusiness.size === 0) return;
    setPendingIncharge(value);
    setIsInchargeConfirmOpen(true);
  };

  const closeInchargeConfirm = () => {
    if (isInchargeSubmitting) return;
    setIsInchargeConfirmOpen(false);
    setPendingIncharge("");
  };

  const confirmAssignIncharge = () => {
    if (!pendingIncharge || checkedBusiness.size === 0) return;

    setIsInchargeSubmitting(true);
    const incharge =
      pendingIncharge === NO_INCHARGE_OPTION_VALUE ? null : pendingIncharge;
    const updateData = Array.from(checkedBusiness).map((businessId) => ({
      businessId,
      incharge,
    }));

    updateIncharge(updateData)
      .then(() => {
        setIsInchargeSubmitting(false);
        setIsInchargeConfirmOpen(false);
        setPendingIncharge("");
        setCheckedBusiness(new Set());
        setIsAssignMode(false);

        const tmpAlertData = { ...alertDialogData };
        tmpAlertData.type = "success";
        tmpAlertData.message = `담당자가 정상적으로 수정되었습니다.\n변경된 담당자를 반영하여 업체 현황을 갱신합니다.`;
        setAlertDialogData(tmpAlertData);
        setIsAlertOpen(true);
        loadStoreList(storeType);
      })
      .catch((err) => {
        console.error(err);
        setIsInchargeSubmitting(false);
        const tmpAlertData = { ...alertDialogData };
        tmpAlertData.type = "error";
        tmpAlertData.message =
          err.response?.data.message || "담당자 수정 중 오류가 발생하였습니다.";
        setAlertDialogData(tmpAlertData);
        setIsAlertOpen(true);
      });
  };

  const baseBusinessList = isFiltered ? filteredBusinessList : sortedBusinessList;
  const displayedBusinessList =
    isMyChargeOnly && myName
      ? baseBusinessList.filter((business) => business.incharge === myName)
      : baseBusinessList;

  // 검색어/내 담당만 보기 필터를 바꿔도 선택은 유지되므로, 선택된 업체 중
  // 지금은 필터에 가려져 화면에 보이지 않는 건수를 따로 알려준다 — 그래야
  // 일괄 배정 시 화면에 안 보이는 업체까지 포함된다는 사실을 놓치지 않는다.
  const displayedBusinessIds = new Set(
    displayedBusinessList.map((business) => business.id)
  );
  const hiddenCheckedCount = Array.from(checkedBusiness).filter(
    (id) => !displayedBusinessIds.has(id)
  ).length;

  const pendingInchargeLabel =
    pendingIncharge === NO_INCHARGE_OPTION_VALUE ? "담당자 없음" : pendingIncharge;

  return (
    <div>
      <section
        css={css`
          padding: 48px 0;
          display: flex;
          align-items: center;
        `}
      >
        <h1
          css={css`
            font-size: 28px;
            font-weight: 500;
          `}
        >
          전체 업체 현황
        </h1>

        <SubtitleCard>
          <ChartIcon />
          <span>전체 ({summaryData.total})</span>
          <CompletedIcon />
          <span>완료 ({summaryData.completed})</span>
          <UncompletedIcon />
          <span>미완료 ({summaryData.uncompleted})</span>
          <TotalIcon />
          <span>데이터 총계 ({summaryData.dataTotal})</span>
        </SubtitleCard>
      </section>

      <section
        css={css`
          display: flex;
          justify-content: space-between;
          flex-wrap: nowrap;
        `}
      >
        <div
          css={css`
            display: flex;
            align-items: center;
            gap: 12px;
            flex-wrap: nowrap;
          `}
        >
          <BaseSelect
            name="반기/분기"
            options={reverseArray(HALF_AND_QUARTERS)}
            selectedOption={convertDateToPeriod(spStartDate, spEndDate)}
            onSelect={onSelectQuarter}
          />
          <BaseSelect
            name="월별검색"
            options={reverseArray(MONTHS)}
            selectedOption={convertDateToPeriod(spStartDate, spEndDate)}
            onSelect={onSelectMonth}
          />
          <BaseInput
            placeholder="업체명 검색"
            ref={inputKeyword}
            onInput={inputKeywordHandler}
            css={css`
              box-sizing: border-box;
            `}
          />
          <BaseButton
            variant={isAssignMode ? "contained" : "outlined"}
            color={PALETTE.primary}
            onClick={toggleAssignMode}
            css={css`
              padding: 11px 16px;
              font-size: 14px;
              white-space: nowrap;
            `}
          >
            담당자 설정
          </BaseButton>
          {isAssignMode && (
            <>
              <span
                css={css`
                  color: ${PALETTE.textMid};
                  font-size: 14px;
                  white-space: nowrap;
                `}
              >
                {checkedBusiness.size}건 선택됨
                {hiddenCheckedCount > 0 &&
                  ` (화면에 없는 ${hiddenCheckedCount}건 포함)`}
              </span>
              <BaseSelect
                name={
                  isInchargeOptionsLoading ? "담당자 조회 중" : "담당자 선택"
                }
                options={[
                  ...inchargeOptions.map((name) => ({
                    value: name,
                    label: name,
                  })),
                  { value: NO_INCHARGE_OPTION_VALUE, label: "(담당자 없음)" },
                ]}
                selectedOption=""
                onSelect={selectIncharge}
                disabled={
                  isInchargeOptionsLoading || checkedBusiness.size === 0
                }
              />
            </>
          )}
        </div>

        <div
          css={css`
            display: flex;
            align-items: center;
            justify-content: flex-end;
            gap: 12px;
            white-space: nowrap;
            flex-shrink: 0;

            label {
              display: flex;
              align-items: center;
              gap: 6px;
              font-size: 14px;
              color: ${PALETTE.textMid};
              cursor: pointer;
            }
          `}
        >
          <BaseCheck
            id="myChargeOnly"
            checked={isMyChargeOnly}
            onChange={(e) => setIsMyChargeOnly(e.target.checked)}
            label="내 담당만 보기"
          />
          {/* <BaseButton
            variant="outlined"
            color={PALETTE.primary}
            disabled={checkedBusiness.size === 0}
            onClick={() => console.log(checkedBusiness)}
          >
            선택 다운로드
          </BaseButton> */}
        </div>
        {/* <ReportButton
          storeType={storeType}
          reportType="submit"
          bizId={
            checkedBusiness.size === 1 && checkedBusiness.values().next().value
          }
          successCallback={() => loadStoreList(storeType)}
        /> */}
        <AlertDialog
          isOpen={isAlertOpen}
          setIsOpen={setIsAlertOpen}
          alertData={alertDialogData}
        />

        <Dialog open={isInchargeConfirmOpen} onClose={closeInchargeConfirm}>
          <DialogTitle>알림</DialogTitle>
          <DialogContent>
            <DialogContentText>
              선택한 {checkedBusiness.size}건을 "{pendingInchargeLabel}" 담당자로
              지정하시겠습니까?
            </DialogContentText>
            {hiddenCheckedCount > 0 && (
              <DialogContentText>
                이 중 {hiddenCheckedCount}건은 현재 필터에 가려져 화면에
                보이지 않는 업체입니다.
              </DialogContentText>
            )}
          </DialogContent>
          <DialogActions
            css={css`
              margin: 0 12px;
              padding: 12px;
              border-top: 1px solid ${PALETTE.border};
            `}
          >
            <BaseButton
              variant="outlined"
              color={PALETTE.primary}
              disabled={isInchargeSubmitting}
              onClick={closeInchargeConfirm}
              css={css`
                padding: 8px 16px;
              `}
            >
              취소
            </BaseButton>
            <BaseButton
              variant="contained"
              color={PALETTE.primary}
              disabled={isInchargeSubmitting}
              onClick={confirmAssignIncharge}
              css={css`
                padding: 8px 16px;
              `}
            >
              확인
            </BaseButton>
          </DialogActions>
        </Dialog>
      </section>

      <section
        css={css`
          margin-top: 12px;
        `}
      >
        <Table
          isLoading={isLoading}
          minWidth={1200}
          columns={columnList}
          dataList={displayedBusinessList}
          menuType={menuType}
          selectable={isAssignMode}
          rowSelectOnly={isAssignMode}
          checkedSet={checkedBusiness}
          setCheckedSet={setCheckedBusiness}
          loadData={() => loadStoreList(storeType)}
        />
      </section>
    </div>
  );
}

export default Store;
